import type { CuratedContentItem } from "@/types/content";
import { SHOW_INFO, CATEGORY_PILL_COLORS } from "@/data/curatedContent";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, ThumbsUp, ThumbsDown, ExternalLink } from "lucide-react";

const EFFORT_LABELS: Record<string, string> = {
  Low: "Easy Win",
  Medium: "Medium Effort",
  High: "Deep Dive",
};

interface FeedItemProps {
  item: CuratedContentItem;
  feedback?: "up" | "down";
  onClick: () => void;
  onFeedback: (item: CuratedContentItem, fb: "up" | "down") => void;
  onBuild: () => void;
}

export function FeedItem({ item, feedback, onClick, onFeedback, onBuild }: FeedItemProps) {
  const show = SHOW_INFO[item.show];
  const pill = CATEGORY_PILL_COLORS[item.category];

  return (
    <div
      className="flex flex-col sm:flex-row border-b border-border"
      style={{ gap: 24, padding: "32px 0" }}
    >
      {/* Thumbnail */}
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => e.key === "Enter" && onClick()}
        className="shrink-0 overflow-hidden cursor-pointer order-first sm:order-last w-full sm:w-[200px] h-[180px] sm:h-[120px] rounded-lg sm:rounded-md"
      >
        <img
          src={item.imageUrl}
          alt={item.title}
          className="w-full h-full object-contain bg-muted"
          loading="lazy"
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Show + creator at top */}
        <div
          className="flex items-center gap-1.5 text-muted-foreground"
          style={{ fontSize: 13, lineHeight: "18px", marginBottom: 8 }}
        >
          {show && (
            <img src={show.logo} alt={item.show} className="h-4 w-4 rounded-full object-cover" />
          )}
          <span>{item.show}</span>
          <span>·</span>
          <span>{show?.host}</span>
        </div>
        <h3
          className="text-foreground font-semibold cursor-pointer hover:text-primary transition-colors"
          style={{ fontSize: 22, lineHeight: "30px", marginBottom: 8 }}
          onClick={onClick}
        >
          {item.title}
        </h3>
        <p
          className="text-foreground/80 line-clamp-2"
          style={{ fontSize: 16, lineHeight: "24px", marginBottom: 12 }}
        >
          {item.description}
        </p>
        {/* Pills row */}
        <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 12 }}>
          {pill && (
            <Badge variant="secondary" className={`text-xs ${pill.bg} ${pill.text} ${pill.border}`}>
              {item.category}
            </Badge>
          )}
          <Badge variant="secondary" className={`text-xs ${EFFORT_LABELS[item.effort] === "Easy Win" ? "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30" : EFFORT_LABELS[item.effort] === "Deep Dive" ? "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30" : "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30"}`}>
            {EFFORT_LABELS[item.effort] || item.effort}
          </Badge>
        </div>
        <div className="flex items-center" style={{ gap: 20, fontSize: 14 }}>
          <button
            onClick={(e) => { e.stopPropagation(); onBuild(); }}
            className="text-primary hover:underline font-medium flex items-center gap-1"
          >
            Build <ArrowRight className="h-3 w-3" />
          </button>
          <a
            href={item.listenUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-muted-foreground hover:text-primary flex items-center gap-1"
          >
            Listen <ExternalLink className="h-3 w-3" />
          </a>
          <div className="flex items-center gap-1 ml-auto">
            <Button
              size="sm"
              variant="ghost"
              className={`h-7 w-7 p-0 ${feedback === "down" ? "text-red-600 dark:text-red-400" : "text-muted-foreground/40 hover:text-red-600 dark:hover:text-red-400"}`}
              onClick={(e) => { e.stopPropagation(); onFeedback(item, "down"); }}
            >
              <ThumbsDown className={`h-3.5 w-3.5 ${feedback === "down" ? "fill-current" : ""}`} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className={`h-7 w-7 p-0 ${feedback === "up" ? "text-green-600 dark:text-green-400" : "text-muted-foreground/40 hover:text-green-600 dark:hover:text-green-400"}`}
              onClick={(e) => { e.stopPropagation(); onFeedback(item, "up"); }}
            >
              <ThumbsUp className={`h-3.5 w-3.5 ${feedback === "up" ? "fill-current" : ""}`} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
